/* eslint-disable */
"use client";;
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import type { FooterTranslations } from '../../i18n/translationProps';
import { FacebookIcon, TwitterIcon, InstagramIcon, YouTubeIcon, SendIcon, TelegramIcon, GitHubIcon } from '../icons';
import { gatewayClient } from '../../api';
import { useCookieConsent } from '../../context/CookieConsentContext';
import { SITE_CONFIG } from '../../../constants';
import fullLogoWhite from '../../assets/images/full-logo-white.svg';
import Image from 'next/image';
import categoriesData from '../../utils/categories.json';
import { ThemeToggle } from '../../theme/ThemeToggle';
import styles from './Footer.module.css';

export interface FooterProps {
  translations: FooterTranslations;
}

export const Footer: React.FC<FooterProps> = ({ translations }) => {
  const t = translations;
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const { localizeFullPath } = useLocalizedPath();
  const { resetConsent } = useCookieConsent();

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 6000);
    return () => clearTimeout(timer);
  }, [message]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const response = await gatewayClient.newsletter.subscribe(email);
      if (!response.message) {
        setMessage({
          type: 'success',
          text: t.subscribeSuccess
        });
        setEmail('');
      } else {
        const message = response.message ?? t.subscribeFailed
        setMessage({ type: 'warning', text: message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: t.subscribeFailed });
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className={styles.footerWrapper}>
      <div className={styles.footerContainer}>
        <div className={styles.footerTop}>
          <div className={styles.footerSection}>
            <div className={styles.footerLogo}>
              {/* 150×30, not 150×36: the logo is 300×61, so with height:auto a
                  150px-wide render resolves to ~30.5px. Declaring 36 made the
                  rendered height disagree with the prop while the width agreed,
                  which is the one-sided mismatch next/image warns about on
                  every page load. */}
              <Image
              src={fullLogoWhite}
              alt="ACEB Logo"
              unoptimized
              width={150}
              height={30}
              priority
              style={{ height: 'auto' }}
            />
            </div>
            <p className={styles.footerDescription}>
              {t.description}
            </p>
            <div className={styles.socialLinks}>
              <a className={styles.socialLink} href={SITE_CONFIG.social.facebook} aria-label="Facebook" target="_blank" rel="noopener">
                <FacebookIcon fontSize="small" />
              </a>
              <a className={styles.socialLink} href={SITE_CONFIG.social.twitter} aria-label="X (Twitter)" target="_blank" rel="noopener">
                <TwitterIcon fontSize="small" />
              </a>
              <a className={styles.socialLink} href={SITE_CONFIG.social.instagram} aria-label="Instagram" target="_blank" rel="noopener">
                <InstagramIcon fontSize="small" />
              </a>
              <a className={styles.socialLink} href={SITE_CONFIG.social.youtube} aria-label="YouTube" target="_blank" rel="noopener">
                <YouTubeIcon fontSize="small" />
              </a>
              <a className={styles.socialLink} href={SITE_CONFIG.social.tiktok} aria-label="TikTok" target="_blank" rel="noopener">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="20px" height="20px" aria-hidden="true"><path d="M12.75 2h2.25c.41 2.09 2.01 3.68 4.01 3.93v2.07c-.82-.02-1.62-.17-2.37-.45v7.7c0 3.31-2.69 6-6 6s-6-2.69-6-6 2.69-6 6-6c.26 0 .52.02.77.06v2.07A3.93 3.93 0 0 0 6.75 16c0 2.21 1.79 4 4 4s4-1.79 4-4V4.13c.73.3 1.52.47 2.37.47V2.07c-2-.25-3.6-1.84-4.01-3.93h-2.25v2z" /></svg>
              </a>
              <a className={styles.socialLink} href={SITE_CONFIG.social.telegram} aria-label="Telegram" target="_blank" rel="noopener">
                <TelegramIcon fontSize="small" />
              </a>
              <a className={styles.socialLink} href={SITE_CONFIG.social.github} aria-label="GitHub" target="_blank" rel="noopener">
                <GitHubIcon fontSize="small" />
              </a>
            </div>
            
            <h3 className={styles.footerTitle} style={{ marginTop: '2rem' }}>{t.newsletter}</h3>
            <p className={styles.footerDescription}>
              {t.newsletterDescription}
            </p>
            <form className={styles.newsletterForm} onSubmit={handleSubmit}>
              <input
                className={styles.newsletterInput}
                type="email"
                placeholder={t.emailPlaceholder}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
              <button className={styles.subscribeButton} type="submit" disabled={loading} aria-label={t.subscribe}>
                <SendIcon fontSize="small" />
              </button>
            </form>
          </div>

          <div className={styles.footerSection}>
            <h3 className={styles.footerTitle}>{t.quickLinks}</h3>
            <Link href={localizeFullPath('/cart')}>
              <span className={styles.footerLink}>{t.myCart}</span>
            </Link>
            <Link href={localizeFullPath('/giftcards')}>
              <span className={styles.footerLink}>{t.giftCards}</span>
            </Link>
            <Link href={localizeFullPath('/blog')}>
              <span className={styles.footerLink}>{t.blog}</span>
            </Link>
            
            <h3 className={styles.footerTitle} style={{ marginTop: '1.5rem' }}>{t.information}</h3>
            <Link href={localizeFullPath('/about')} prefetch={false}>
              <span className={styles.footerLink}>{t.aboutUs}</span>
            </Link>
            <Link href={localizeFullPath('/contact')} prefetch={false}>
              <span className={styles.footerLink}>{t.contact}</span>
            </Link>
            <Link href={localizeFullPath('/faq')} prefetch={false}>
              <span className={styles.footerLink}>{t.faq}</span>
            </Link>
          </div>

          <div className={styles.categoriesSection}>
            <h3 className={styles.footerTitle}>{t.categories}</h3>
            <div className={styles.categoriesGrid}>
              {categoriesData.map((category) => (
                <Link 
                  key={category.Slug} 
                  href={localizeFullPath(`/giftcards/${category.Slug}`)}
                >
                  <span className={styles.footerLink}>
                    {t.categoryNames[category.Slug] || category.Name}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.footerDivider} />

        <div className={styles.legalSection}>
          <span><strong>{SITE_CONFIG.legal.companyName}</strong></span>
          <span>•</span>
          <span>CUI: {SITE_CONFIG.legal.cui}</span>
          <span>•</span>
          <span>Reg. Com.: {SITE_CONFIG.legal.regCom}</span>
          <span>•</span>
          <span>{SITE_CONFIG.legal.address}, {SITE_CONFIG.legal.postalCode} {SITE_CONFIG.legal.city}, {SITE_CONFIG.legal.county}, {SITE_CONFIG.legal.country}</span>
          <span>•</span>
          <span>Email: <a href={`mailto:${SITE_CONFIG.contact.email}`}>{SITE_CONFIG.contact.email}</a></span>
        </div>

        <div className={styles.footerBottom}>
          <p className={styles.copyright}>
            {currentYear} {t.copyright}
          </p>

          <div className={styles.footerLinks}>
            <Link href={localizeFullPath('/privacy')} prefetch={false}>
              <span className={styles.footerBottomLink}>{t.privacyPolicy}</span>
            </Link>
            <Link href={localizeFullPath('/terms')} prefetch={false}>
              <span className={styles.footerBottomLink}>{t.termsOfService}</span>
            </Link>
            <Link href={localizeFullPath('/return')} prefetch={false}>
              <span className={styles.footerBottomLink}>{t.returnPolicy}</span>
            </Link>
            <Link href={localizeFullPath('/cookie-policy')} prefetch={false}>
              <span className={styles.footerBottomLink}>{t.cookiePolicy}</span>
            </Link>
            <Link href={localizeFullPath('/cashback-terms')} prefetch={false}>
              <span className={styles.footerBottomLink}>{t.cashbackTerms}</span>
            </Link>
            <Link href={localizeFullPath('/referral-terms')} prefetch={false}>
              <span className={styles.footerBottomLink}>{t.referralTerms}</span>
            </Link>
            <button
              onClick={resetConsent}
              className={styles.footerBottomLink}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, font: 'inherit' }}
            >
              {t.cookiePreferences}
            </button>
            <ThemeToggle />
          </div>
        </div>
      </div>

      <div className={`${styles.snackbar}${message ? ` ${styles.visible}` : ''}`}>
        {message && (
          <div className={`${styles.alert} ${styles[message.type]}`}>
            <span>{message.text}</span>
            <button className={styles.alertClose} onClick={() => setMessage(null)}>×</button>
          </div>
        )}
      </div>
    </footer>
  );
}; 